import { useTranslation } from "react-i18next";
import {
  TCompetitionFormat,
  TCompetitionFormatHoles,
  TCompetitionHandicap,
  TCompetitionOrganization,
  TCompetitionScoreCombo,
  TCompetitionTeamScores,
  TPlayerCompetition,
  TTeamCompetition,
  TCompetitionFormatPointAllocation,
} from "redux/reducers/models/league";

export default function useLeagueScoringFormatOptions() {
  const { t } = useTranslation();

  const organizationOptions: Array<{ id: TCompetitionOrganization; label: string }> = [
    { id: "individual", label: t("Individual") },
    { id: "team", label: t("Teams") },
  ];

  const formatOptions: Array<{ id: TCompetitionFormat; label: string }> = [
    { id: "stableford", label: t("Stableford") },
    { id: "stroke_play", label: t("Stroke Play") },
    { id: "points_by_score", label: t("Points by Score") },
    { id: "skins", label: t("Skins") },
  ];

  const formatHoleOptions: Array<{ id: TCompetitionFormatHoles; label: string }> = [
    { id: "9_holes", label: t("9 Holes") },
    { id: "18_holes", label: t("18 Holes") },
  ];

  const handicapOptions: Array<{ id: TCompetitionHandicap; label: string }> = [
    { id: "gross", label: t("Gross") },
    { id: "internal_net", label: t("Net") },
  ];

  const scoreComboOptions: Array<{ id: TCompetitionScoreCombo; label: string }> = [
    { id: "all", label: t("All") },
    { id: "gross_first", label: t("Gross First") },
    { id: "net_first", label: t("Net First") },
  ];

  const teamScoreOptions: Array<{ id: TCompetitionTeamScores; label: string }> = [
    { id: "gross_scores", label: t("Gross Scores") },
    { id: "net_scores", label: t("Net Scores") },
    { id: "best_scores", label: t("Best Scores") },
  ];

  const playerCompetitionOptions: Array<{
    id: TPlayerCompetition | TTeamCompetition;
    label: string;
    organization: TCompetitionOrganization;
  }> = [
    { id: "player_vs_field", label: t("Player vs. Field"), organization: "individual" },
    { id: "player_vs_flight", label: t("Player vs. Flight"), organization: "individual" },
    { id: "team_vs_field", label: t("Team vs. Field"), organization: "team" },
  ];

  const nineHolePoints: Array<{ id: string; label: string; points: number }> = [
    { id: "four_over", label: t("4 or More"), points: 1 },
    { id: "three_over", label: "3", points: 2 },
    { id: "two_over", label: "2", points: 2 },
    { id: "one_over", label: "1", points: 3 },
    { id: "even", label: t("Even"), points: 3 },
    { id: "one_under", label: "-1", points: 4 },
    { id: "two_under", label: "-2", points: 4 },
    { id: "three_under", label: "-3", points: 5 },
    { id: "four_under", label: "-4", points: 5 },
    { id: "five_under", label: "-5", points: 6 },
    { id: "six_under", label: "-6", points: 6 },
    { id: "seven_under", label: t("-7 or Less"), points: 7 },
  ];

  const eighteenHolePoints: Array<{ id: string; label: string; points: number }> = [
    { id: "eight_over", label: t("8 or More"), points: 1 },
    { id: "seven_over", label: "7", points: 2 },
    { id: "six_over", label: "6", points: 3 },
    { id: "five_over", label: "5", points: 3 },
    { id: "four_over", label: "4", points: 4 },
    { id: "three_over", label: "3", points: 4 },
    { id: "two_over", label: "2", points: 5 },
    { id: "one_over", label: "1", points: 5 },
    { id: "even", label: t("Even"), points: 6 },
    { id: "one_under", label: "-1", points: 7 },
    { id: "two_under", label: "-2", points: 8 },
    { id: "three_under", label: "-3", points: 9 },
    { id: "four_under", label: "-4", points: 10 },
    { id: "five_under", label: "-5", points: 11 },
    { id: "six_under", label: "-6", points: 12 },
    { id: "seven_under", label: "-7", points: 13 },
    { id: "eight_under", label: "-8", points: 14 },
    { id: "nine_under", label: "-9", points: 15 },
    { id: "ten_under", label: t("-10 or Less"), points: 16 },
  ];

  const pointAllocationOptions: Array<{ id: TCompetitionFormatPointAllocation; label: string }> = [
    { id: "posted_points", label: t("Posted Points") },
    { id: "posted", label: t("Posted Score") },
  ];

  return {
    organizationOptions,
    formatOptions,
    formatHoleOptions,
    handicapOptions,
    scoreComboOptions,
    teamScoreOptions,
    playerCompetitionOptions,
    nineHolePoints,
    eighteenHolePoints,
    pointAllocationOptions,
  };
}
